import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { orgTreeApi } from '@/services/org-tree'

const normalizeCountryCode = (value?: string | null) => {
  const code = String(value ?? '').trim().toUpperCase()
  return /^[A-Z]{2}$/.test(code) ? code : ''
}

const normalizeTimeZone = (value?: string | number | null) => {
  const candidate = String(value ?? '').trim()
  if (!candidate || /^\d+$/.test(candidate)) {
    return undefined
  }
  return candidate
}

const resolveCountryLocale = (countryCode?: string | null) => {
  const normalized = normalizeCountryCode(countryCode)
  if (!normalized) {
    return 'en-US'
  }

  const candidates = [`en-${normalized}`, 'en-US']
  return Intl.DateTimeFormat.supportedLocalesOf(candidates)[0] || 'en-US'
}

const buildDateTimeFormatOptions = (
  timeZone?: string,
  dateStyle: Intl.DateTimeFormatOptions = {},
) => ({
  ...dateStyle,
  ...(timeZone ? { timeZone } : {}),
})

export const formatDateForLocale = (
  value?: string | Date | null,
  locale = 'en-US',
  fallback = '-',
  timeZone?: string,
) => {
  if (!value) {
    return fallback
  }

  const parsed = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return typeof value === 'string' ? value : fallback
  }

  return new Intl.DateTimeFormat(
    locale,
    buildDateTimeFormatOptions(timeZone, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
  ).format(parsed)
}

export const formatDateTimeForLocale = (
  value?: string | Date | null,
  locale = 'en-US',
  fallback = '-',
  timeZone?: string,
) => {
  if (!value) {
    return fallback
  }

  const parsed = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return typeof value === 'string' ? value : fallback
  }

  return new Intl.DateTimeFormat(
    locale,
    buildDateTimeFormatOptions(timeZone, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
  ).format(parsed)
}

export const formatTimeForLocale = (
  value?: string | Date | null,
  locale = 'en-US',
  fallback = '-',
  timeZone?: string,
) => {
  if (!value) {
    return fallback
  }

  const parsed = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return typeof value === 'string' ? value : fallback
  }

  return new Intl.DateTimeFormat(
    locale,
    buildDateTimeFormatOptions(timeZone, {
      hour: 'numeric',
      minute: '2-digit',
    }),
  ).format(parsed)
}

const formatAssignmentDateTimeForLocale = (
  value?: string | Date | null,
  locale = 'en-US',
  fallback = '—',
  timeZone?: string,
) => {
  if (!value) {
    return fallback
  }

  const parsed = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return typeof value === 'string' ? value : fallback
  }

  return new Intl.DateTimeFormat(
    locale,
    buildDateTimeFormatOptions(timeZone, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
  ).format(parsed)
}

const formatAssignmentDateForLocale = (
  value?: string | Date | null,
  locale = 'en-US',
  fallback = '—',
  timeZone?: string,
) => {
  if (!value) {
    return fallback
  }

  const parsed = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return typeof value === 'string' ? value : fallback
  }

  return new Intl.DateTimeFormat(
    locale,
    buildDateTimeFormatOptions(timeZone, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }),
  ).format(parsed)
}

export const getHourFractionInTimeZone = (value: Date, timeZone?: string) => {
  if (!timeZone) {
    return value.getHours() + value.getMinutes() / 60
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(value)

  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? 0)
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? 0)
  return hour + minute / 60
}

export const formatAssignmentScheduleForLocale = (
  assignment: { start_date?: string | null; end_date?: string | null },
  locale = 'en-US',
  fallback = '—',
  timeZone?: string,
) => {
  const start = assignment.start_date
  const end = assignment.end_date

  if (!start && !end) {
    return fallback
  }

  if (start && end) {
    const parsedStart = new Date(start)
    const parsedEnd = new Date(end)
    const hasValidRange = !Number.isNaN(parsedStart.getTime()) && !Number.isNaN(parsedEnd.getTime())

    if (hasValidRange && parsedStart.getTime() === parsedEnd.getTime()) {
      return formatAssignmentDateTimeForLocale(start, locale, fallback, timeZone)
    }

    if (
      hasValidRange &&
      formatAssignmentDateForLocale(start, locale, fallback, timeZone) === formatAssignmentDateForLocale(end, locale, fallback, timeZone)
    ) {
      return `${formatAssignmentDateTimeForLocale(start, locale, fallback, timeZone)} – ${formatTimeForLocale(end, locale, fallback, timeZone)}`
    }

    return `${formatAssignmentDateTimeForLocale(start, locale, fallback, timeZone)} – ${formatAssignmentDateTimeForLocale(end, locale, fallback, timeZone)}`
  }

  return formatAssignmentDateTimeForLocale(start ?? end, locale, fallback, timeZone)
}

export const useOrganizationDateLocale = (serviceInstanceId?: string | null) => {
  const instanceQuery = useQuery({
    queryKey: ['org-date-locale-instance', serviceInstanceId],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(String(serviceInstanceId)),
    enabled: Boolean(serviceInstanceId),
    staleTime: 5 * 60_000,
  })

  const organizationCountryCode = useMemo(
    () => normalizeCountryCode(instanceQuery.data?.country_code),
    [instanceQuery.data?.country_code],
  )

  return useMemo(() => resolveCountryLocale(organizationCountryCode), [organizationCountryCode])
}

export const useOrganizationTimeZone = (serviceInstanceId?: string | null) => {
  const instanceQuery = useQuery({
    queryKey: ['org-date-locale-instance', serviceInstanceId],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(String(serviceInstanceId)),
    enabled: Boolean(serviceInstanceId),
    staleTime: 5 * 60_000,
  })

  return useMemo(() => normalizeTimeZone(instanceQuery.data?.time_zone), [instanceQuery.data?.time_zone])
}

export const useOrganizationDateTimeFormat = (serviceInstanceId?: string | null) => {
  const locale = useOrganizationDateLocale(serviceInstanceId)
  const timeZone = useOrganizationTimeZone(serviceInstanceId)

  return useMemo(
    () => ({
      locale,
      timeZone,
      formatDate: (value?: string | Date | null, fallback = '-') =>
        formatDateForLocale(value, locale, fallback, timeZone),
      formatDateTime: (value?: string | Date | null, fallback = '-') =>
        formatDateTimeForLocale(value, locale, fallback, timeZone),
      formatTime: (value?: string | Date | null, fallback = '-') =>
        formatTimeForLocale(value, locale, fallback, timeZone),
      getHourFraction: (value: Date) => getHourFractionInTimeZone(value, timeZone),
    }),
    [locale, timeZone],
  )
}

export const preferOrgLocalDateTime = (
  utcValue?: string | null,
  localValue?: string | null,
) => {
  const local = String(localValue ?? '').trim()
  if (local) {
    return local
  }
  return utcValue ?? null
}
